import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';

const TeachingSpeaking = () => {
  const title = 'Teaching Speaking';
  const image = '/Images/speaking.webp';
  const intro = 'Speaking is the skill students most want and often fear most. This guide covers fluency vs. accuracy balance, task design for real communication, feedback that builds confidence, and classroom management for speaking activities.';

  const seoContent = [
    'Speaking lessons live or die by task design. A \"discuss this topic\" prompt produces silence or monologues. A well-designed task has: a clear outcome (decide, rank, plan, solve), an information gap (each student holds different pieces), a reason to listen (you need your partner\'s info to complete the task), and a time limit. This creates genuine communication, not performance.',
    'Balance fluency and accuracy through task staging. Early stages (warm-up, fluency tasks): prioritize message, delay correction, encourage risk-taking. Later stages (focus on form, prepared talks): Zoom in on specific errors, provide models, allow rehearsal. The \"fluency first, accuracy later\" principle prevents the accuracy trap where students self-monitor into silence.',
    'Error correction during speaking: use a taxonomy. 1) Recast (implicit, flow-preserving): S: \"He go yesterday.\" T: \"Ah, he went yesterday?\" 2) Prompt (metalinguistic): \"Past tense?\" 3) Explicit correction (last resort): \"Say \'went.\'\" Match technique to error type (slip vs. gap) and lesson stage (fluency vs. accuracy).',
    'Manage speaking logistics: pairs > groups > whole class for talking time. Use \"conversation stations\" (rotate every 3 mins), \"talking chips\" (each student has 3 chips, must spend to speak), or \"spy\" roles (one student observes and reports good language). These structures equalize participation and reduce teacher talk.',
    'Assess speaking formatively with simple rubrics: task completion, vocabulary range, grammar control, pronunciation, interaction. Share criteria beforehand. Record 1-minute samples monthly—students self-assess using the rubric, you compare. Progress becomes visible and motivating.',
  ];

  const levels = [
    {
      level: 'tasks',
      label: 'Speaking Task Types',
      tagline: 'From controlled to free',
      cards: [
        {
          title: 'Information Gap Library',
          image: '/Images/grammar1.webp',
          desc: '20+ ready tasks: spot-the-difference, route-finding, budget allocation, mystery solve.',
          links: [{ text: 'Browse Tasks', to: '/speaking-exercises/info-gaps' }],
        },
        {
          title: 'Discussion Frameworks',
          image: '/Images/2people1.webp',
          desc: 'Pyramid discussion, fishbowl, four corners, silent debate—step-by-step guides.',
          links: [{ text: 'View Frameworks', to: '/speaking-exercises/discussion' }],
        },
        {
          title: 'Role-Play & Simulation Bank',
          image: '/Images/vocabulary.webp',
          desc: 'Situation cards (complaint, negotiation, interview) with role cards and useful language.',
          links: [{ text: 'Download Pack', to: '/speaking-exercises/roleplays' }],
        },
      ],
    },
    {
      level: 'feedback',
      label: 'Feedback & Correction',
      tagline: 'Build confidence, not anxiety',
      cards: [
        {
          title: 'Correction Technique Card',
          image: '/Images/online-teacher.webp',
          desc: 'Laminated quick-ref: recast, prompt, explicit, delayed feedback—when to use each.',
          links: [{ text: 'Download', to: '/resources/correction-card.pdf' }],
        },
        {
          title: 'Speaking Rubric Templates',
          image: '/Images/tutor2-1280x470.webp',
          desc: 'CEFR-aligned analytic & holistic rubrics for A2–C1, editable in Google Docs.',
          links: [{ text: 'Get Rubrics', to: '/resources/speaking-rubrics' }],
        },
        {
          title: 'Self-Assessment Exit Tickets',
          image: '/Images/3people1a.webp',
          desc: '3-question slips: \"What did you say well? What word did you need? One goal for next time.\"',
          links: [{ text: 'Download', to: '/resources/speaking-exit.pdf' }],
        },
      ],
    },
  ];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={levels}
      seoTitle="Teaching Speaking: Task Design, Fluency/Accuracy Balance & Feedback Techniques"
      seoDescription="Practical guide for ESL speaking lessons. Information-gap tasks, discussion frameworks, role-plays, correction taxonomy (recast/prompt/explicit), participation structures, and free downloadable rubrics and task banks."
    />
  );
};

export default TeachingSpeaking;